import { useLocation } from "react-router-dom";
import { useMemo } from "react";

import { useAllSaasDestinations } from "@/hooks/useAllSaasDestinations";
import { SaaSDestination } from "@/destinations/destinations";
import { getItem, removeItem, setItem } from "@/utils/sessionStorage";

const CATEGORY_TO_NAV_MAPPING = {
  "Data activation": "/destinations/data-activation",
  "Data warehouse": "/destinations/warehouses",
  "Direct response ads": "/destinations/direct-response-ads",
  "Demand side platform": "/destinations/demand-side-platforms",
  Audio: "/destinations/audio",
  Audiences: "/destinations/audiences",
  CTV: "/destinations/ctv",
  MMP: "/destinations/mmp",
} as const;

// Reverse mapping: URL -> Category name
const NAV_TO_CATEGORY_MAPPING: Record<
  string,
  keyof typeof CATEGORY_TO_NAV_MAPPING
> = {
  "/destinations/data-activation": "Data activation",
  "/destinations/warehouses": "Data warehouse",
  "/destinations/direct-response-ads": "Direct response ads",
  "/destinations/demand-side-platforms": "Demand side platform",
  "/destinations/audio": "Audio",
  "/destinations/audiences": "Audiences",
  "/destinations/ctv": "CTV",
  "/destinations/mmp": "MMP",
};

const LAST_CATEGORY_STORAGE_KEY = "fp_last_destination_category";

/**
 * Gets the last visited category from sessionStorage
 */
function getLastCategoryFromStorage(): string | null {
  return getItem(LAST_CATEGORY_STORAGE_KEY);
}

/**
 * Stores the category in sessionStorage when on a category page
 */
function storeCategoryInStorage(category: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (category) {
      setItem(LAST_CATEGORY_STORAGE_KEY, category);
    } else {
      removeItem(LAST_CATEGORY_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Utility function to get the destination category URL from a destination object.
 * If a preferred category is provided and it's in the destination's categories, use it.
 * Otherwise, falls back to the first category.
 */
function getCategoryUrlFromDestination(
  destination: SaaSDestination,
  preferredCategory?: string | null,
): string | null {
  if (destination && destination.destinationCategories.length > 0) {
    // If a preferred category is provided and it's in the destination's categories, use it
    if (
      preferredCategory &&
      destination.destinationCategories.includes(
        preferredCategory as (typeof destination.destinationCategories)[number],
      )
    ) {
      return (
        CATEGORY_TO_NAV_MAPPING[
          preferredCategory as keyof typeof CATEGORY_TO_NAV_MAPPING
        ] || null
      );
    }

    // Otherwise, fall back to the first category
    return (
      CATEGORY_TO_NAV_MAPPING[
        destination
          .destinationCategories[0] as keyof typeof CATEGORY_TO_NAV_MAPPING
      ] || null
    );
  }
  return null;
}

/**
 * Pure function to determine active destination category from pathname and destinations
 */
function getActiveDestinationCategoryFromPath(
  pathname: string,
  allSaasDestinations: Record<string, any>,
): string | null {
  if (!pathname) {
    return null;
  }

  // Direct category page matches - store the category when on a category page
  for (const [url, category] of Object.entries(NAV_TO_CATEGORY_MAPPING)) {
    if (pathname.startsWith(url)) {
      storeCategoryInStorage(category);
      return url;
    }
  }

  // Handle /destinations/apps/[destination-slug]/[config-id] URLs
  const appsMatch = pathname.match(/^\/destinations\/apps\/([^\/]+)/);
  if (appsMatch) {
    const destinationSlug = appsMatch[1];
    const destination = allSaasDestinations[destinationSlug];

    if (destination) {
      // Try to determine the category from sessionStorage
      const storedCategory = getLastCategoryFromStorage();
      return getCategoryUrlFromDestination(destination, storedCategory);
    }
  }

  if (pathname.startsWith("/destinations/overview")) {
    storeCategoryInStorage(null); // Clear category when on overview
    return "/destinations/overview";
  }

  if (pathname.startsWith("/destinations/catalog")) {
    storeCategoryInStorage(null); // Clear category when on catalog
    return "/destinations/catalog";
  }

  if (pathname.startsWith("/consent-management")) {
    return "/consent-management";
  }

  return null;
}

/**
 * Hook to determine which destination category navigation item should be active
 * based on the current URL. Handles both category pages and individual destination config pages.
 */
export function useActiveDestinationCategory(): string | null {
  const location = useLocation();
  const allSaasDestinations = useAllSaasDestinations();

  return useMemo(() => {
    return getActiveDestinationCategoryFromPath(
      location.pathname,
      allSaasDestinations,
    );
  }, [location.pathname, allSaasDestinations]);
}
